'use client';

import { useEffect } from 'react';
import IdleTimer from 'react-idle-timer';
import { toast } from 'react-toastify';

import jsCookie from 'js-cookie';
import { useRouter } from 'next/navigation';

import { REMEMBER_ME_SESSION_IDLE_DURATION_HOURS, REMEMBER_ME_SESSION_TRACKING_KEY } from '@/config/constants';
import { useIdleTracking } from '@/helpers/hooks/useIdleTracking';
import { useLogout } from '@/helpers/hooks/useLogout';
import useWebAlias from '@/helpers/hooks/useWebalias';
import {
  isSessionCookieExpired,
  markSessionAsIdleExpired,
  updateSessionCookie,
} from '@/helpers/utils/idleTrackingUtils';
import { useAppContext } from '@/layouts/AppProvider';
import { RootState, useAppSelector } from '@/store';

const IDLE_TIMEOUT = 1000 * 60 * 60 * REMEMBER_ME_SESSION_IDLE_DURATION_HOURS; // 2 hours in milliseconds

/**
 * IdleTrackingNotRemember Component
 *
 * This component implements idle tracking for users who haven't selected "Remember Me".
 * It works as follows:
 *
 * 1. When user logs in without "Remember Me", a session tracking cookie is set with current timestamp
 * 2. This component only mounts if the cookie exists and is less than 2 hours old
 * 3. If user becomes idle for 2 hours, it logs them out and removes the cookie
 * 4. If user becomes active before the 2-hour idle timeout, it updates the cookie timestamp
 * 5. During continuous activity, it updates the cookie every 15 minutes to keep session fresh
 *
 * The component is automatically mounted in AuthProvider and will self-manage based on cookie presence.
 */
export const IdleTrackingForNotRemember = ({ isPetPro }: { isPetPro: boolean }) => {
  console.log('🔄 [IdleTracking] Component mounted with isPetPro:', isPetPro);

  // Get authentication status from Redux store as a safety check
  const { isAuthenticated, isAuthenticationLoading } = useAppSelector(
    (state: RootState) => state.slices.authenticationSlice
  );
  const { shouldTrackIdle } = useIdleTracking();
  const router = useRouter();
  const { webalias, generateURL } = useWebAlias();
  const { isBackOffice } = useAppContext();
  const { logout } = useLogout(isBackOffice, webalias);

  console.log('🎯 [IdleTracking] shouldTrackIdle:', shouldTrackIdle);
  console.log(
    '🔐 [IdleTracking] Component auth status - isAuthenticated:',
    isAuthenticated,
    'isLoading:',
    isAuthenticationLoading
  );

  const handleIdleLogout = () => {
    console.log('🚪 [IdleTracking] Executing idle logout - showing toast and marking session as expired');

    // Show toast notification to user
    toast.info('You have been logged out due to inactivity.');

    // Mark session as idle expired (allowing other domains to detect) but don't remove cookie
    // The cookie will be overwritten when user logs in again
    markSessionAsIdleExpired();
    logout();

    console.log('✅ [IdleTracking] Idle logout completed - cookie preserved for cross-domain coordination');
  };

  // 🚨 Check for cross-domain idle timeout and trigger immediate logout
  useEffect(() => {
    if (!isAuthenticated || isAuthenticationLoading) {
      console.log('🔍 [IdleTracking] Skipping idle timeout check - not authenticated or loading');
      return;
    }

    const checkForIdleTimeout = () => {
      console.log('🔍 [IdleTracking] Checking for cross-domain idle timeout...');
      const sessionStatus = isSessionCookieExpired(false); // Don't auto-cleanup
      console.log('🔍 [IdleTracking] Session status for idle timeout check:', sessionStatus);

      if (sessionStatus.exists && sessionStatus.wasIdleTimeout) {
        console.log('🚨 [IdleTracking] Idle timeout detected from another domain - triggering immediate logout');
        handleIdleLogout();
        return true; // Logout was triggered
      } else if (sessionStatus.exists) {
        console.log('✅ [IdleTracking] Session exists but no idle timeout detected');
      } else {
        console.log('❌ [IdleTracking] No session exists during idle timeout check');
      }
      return false;
    };

    // Check immediately
    console.log('🚀 [IdleTracking] Starting cross-domain idle timeout monitoring');
    if (checkForIdleTimeout()) return;

    // Check every 1 minute for immediate coordination
    const idleTimeoutCheck = setInterval(() => {
      if (checkForIdleTimeout()) {
        clearInterval(idleTimeoutCheck);
      }
    }, 60000); // Check every 1 minute (60000ms)

    return () => {
      console.log('🛑 [IdleTracking] Stopping cross-domain idle timeout monitoring');
      clearInterval(idleTimeoutCheck);
    };
  }, [isAuthenticated, isAuthenticationLoading, handleIdleLogout]);

  const handleOnActive = () => {
    // User became active after being idle, update the cookie with current timestamp
    // This resets the session validity period
    console.log('👋 [IdleTracking] User became active from idle state - updating session cookie');
    updateSessionCookie(isPetPro);
  };

  const handleOnIdle = () => {
    // User has been idle for the timeout period - but check if ANY domain has been active
    console.log(
      `💤 [IdleTracking] User has been idle for ${REMEMBER_ME_SESSION_IDLE_DURATION_HOURS} hours - checking global activity before logout`
    );

    // Check one more time if session is still valid (in case of race conditions)
    const sessionStatus = isSessionCookieExpired(false);

    if (!sessionStatus.exists) {
      console.log('🍪 [IdleTracking] No session cookie found during idle logout');
      return;
    }

    if (sessionStatus.isExcluded) {
      console.log('🚫 [IdleTracking] Current domain is excluded, skipping idle logout');
      return;
    }

    // CROSS-DOMAIN ACTIVITY CHECK: Before logging out, verify no other domain has been active
    const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);
    if (sessionCookie) {
      try {
        const cookieData = JSON.parse(sessionCookie);
        const lastGlobalActivity = parseInt(cookieData.timestamp, 10);

        if (!isNaN(lastGlobalActivity)) {
          const timeSinceLastGlobalActivity = Date.now() - lastGlobalActivity;
          const hoursSinceLastGlobalActivity = timeSinceLastGlobalActivity / (1000 * 60 * 60);
          const thresholdHours = REMEMBER_ME_SESSION_IDLE_DURATION_HOURS;

          console.log(
            `🌐 [IdleTracking] Checking global activity: ${hoursSinceLastGlobalActivity.toFixed(
              2
            )} hours since last activity across all domains`
          );

          // If ANY domain was active within the timeout period, don't logout
          if (hoursSinceLastGlobalActivity < thresholdHours) {
            console.log('🌐 [IdleTracking] Another domain was active recently - preventing logout from this domain');
            console.log(
              `🕐 [IdleTracking] Last global activity: ${hoursSinceLastGlobalActivity.toFixed(
                2
              )} hours ago (threshold: ${thresholdHours} hours)`
            );
            return; // Don't logout - user is still active somewhere
          } else {
            console.log('🌐 [IdleTracking] No recent activity detected across any domain - proceeding with logout');
            console.log(
              `🕐 [IdleTracking] Global inactivity: ${hoursSinceLastGlobalActivity.toFixed(
                2
              )} hours (exceeded ${thresholdHours} hour threshold)`
            );
          }
        } else {
          console.warn(
            '⚠️ [IdleTracking] Invalid timestamp in cookie during global activity check - proceeding with logout as fallback'
          );
        }
      } catch (error) {
        console.warn(
          '⚠️ [IdleTracking] Error parsing cookie for global activity check - proceeding with logout as fallback:',
          error
        );
      }
    } else {
      console.warn(
        '⚠️ [IdleTracking] No session cookie found for global activity check - proceeding with logout as fallback'
      );
    }

    // Only logout if no domain has been active within the timeout period
    console.log('🚪 [IdleTracking] Confirmed global inactivity - executing logout');
    handleIdleLogout();
  };

  const handleOnAction = () => {
    // User performed an action (mouse move, key press, etc.)
    // This is called on EVERY activity event, not just when returning from idle
    // We should periodically update the cookie during continuous activity
    // to prevent session expiration for active users
    console.log('User action detected - checking if session cookie needs update');

    // Check session status including domain exclusion - DISABLE auto-cleanup to prevent domain exclusion
    const sessionStatus = isSessionCookieExpired(false); // Disable auto-cleanup

    if (!sessionStatus.exists) {
      console.log('No session cookie found during action');
      return;
    }

    // Handle immediate logout scenarios
    if (sessionStatus.isExcluded) {
      console.log('🚫 [IdleTracking] Current domain is excluded from session, logging out immediately');
      handleIdleLogout();
      return;
    }

    if (sessionStatus.isExpired) {
      console.log('⏰ [IdleTracking] Session expired during action, logging out immediately');
      handleIdleLogout();
      return;
    }

    // Session is valid, check if we need to update it
    const sessionCookie = jsCookie.get(REMEMBER_ME_SESSION_TRACKING_KEY);
    if (sessionCookie) {
      try {
        // Parse the JSON cookie
        const cookieData = JSON.parse(sessionCookie);
        const sessionTime = parseInt(cookieData.timestamp, 10);

        if (isNaN(sessionTime)) {
          console.warn('Invalid timestamp in session cookie');
          return;
        }

        const currentTime = Date.now();
        const timeSinceUpdate = currentTime - sessionTime;
        const minutesSinceUpdate = timeSinceUpdate / (1000 * 60);

        // Update cookie if it's been more than 1 minute since last update
        if (minutesSinceUpdate >= 1) {
          console.log('Updating session cookie due to continued activity (1+ minutes since last update)');
          updateSessionCookie(isPetPro);
        }
      } catch (error) {
        console.warn('Error parsing session cookie:', error);
        // If cookie is corrupted, mark as idle expired and logout
        markSessionAsIdleExpired();
        handleIdleLogout();
      }
    }
  };

  // SAFETY CHECK: Don't render if user is not authenticated
  if (!isAuthenticated || isAuthenticationLoading) {
    console.log('❌ [IdleTracking] User not authenticated or still loading - not rendering timer');
    return null;
  }

  // Only render IdleTimer if we should track idle state
  // (i.e., cookie exists and is less than 2 hours old)
  if (!shouldTrackIdle) {
    console.log('❌ [IdleTracking] Not tracking idle state, shouldTrackIdle:', shouldTrackIdle);
    return null;
  }

  console.log('✅ [IdleTracking] User authenticated and should track idle - rendering IdleTimer');

  return (
    <IdleTimer
      timeout={IDLE_TIMEOUT}
      onActive={handleOnActive}
      onIdle={handleOnIdle}
      onAction={handleOnAction}
      throttle={5000} // Throttle onAction to fire at most every 5 seconds
      eventsThrottle={1000} // Throttle the underlying events to reduce CPU usage
    />
  );
};
